import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {HelpArticleService} from '../../service/api/help-article.service';
import {HelpCategoryService} from '../../service/api/help-category.service';
import {
    HelpArticleCreateRequestDto,
    HelpArticleDto,
    HelpArticleUpdateRequestDto
} from '../../model/dto/help-article.dto';
import {HelpCategoryDto} from '../../model/dto/help-category.dto';

/**
 * Article editor component for CRUD operations on help articles.
 * Uses TinyMCE for rich text editing.
 * Supports create and edit modes.
 */
@Component({
    selector: 'mvs-article-editor',
    templateUrl: './article-editor.component.html',
    styleUrls: ['./article-editor.component.scss'],
    standalone: false
})
export class ArticleEditorComponent implements OnInit, OnChanges, OnDestroy {

    @Input() moduleAlias: string;
    @Input() articleId: number;
    @Output() saved = new EventEmitter<HelpArticleDto>();
    @Output() cancelled = new EventEmitter<void>();

    initialized = false;
    article: HelpArticleDto;
    categories: HelpCategoryDto[] = [];
    isEditMode = false;
    isSaving = false;

    // Form fields
    title = '';
    content = '';
    summary = '';
    selectedCategoryId: number;
    sortOrder = 0;
    tagsJson = '';
    changeNote = '';

    // TinyMCE config
    tinyMceConfig: any = {
        height: 500,
        menubar: true,
        plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
        toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table | link image | code | help',
        content_style: "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 14px; }",
        language: 'de',
        promotion: false,
        branding: false
    };

    private subscriptions: Subscription[] = [];

    constructor(
        private articleService: HelpArticleService,
        private categoryService: HelpCategoryService
    ) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.loadCategories();

        if (this.articleId) {
            this.isEditMode = true;
            this.loadArticle();
        }
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['articleId'] || changes['moduleAlias']) {
            this.initComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private loadCategories(): void {
        this.categoryService.getCategoryTree(this.moduleAlias).subscribe({
            next: categories => this.categories = this.flattenCategories(categories),
            error: err => console.error('Failed to load categories', err)
        });
    }

    private loadArticle(): void {
        this.articleService.getArticle(this.articleId).subscribe({
            next: article => {
                this.article = article;
                this.title = article.title;
                this.content = article.content;
                this.summary = article.summary || '';
                this.selectedCategoryId = article.categoryId;
                this.sortOrder = article.sortOrder;
                this.tagsJson = article.tagsJson || '';
            },
            error: err => console.error('Failed to load article', err)
        });
    }

    onSave(): void {
        if (!this.title.trim() || !this.content.trim()) return;

        this.isSaving = true;

        if (this.isEditMode) {
            const request: HelpArticleUpdateRequestDto = {
                title: this.title,
                content: this.content,
                summary: this.summary,
                categoryId: this.selectedCategoryId,
                sortOrder: this.sortOrder,
                tagsJson: this.tagsJson,
                changeNote: this.changeNote || 'Artikel aktualisiert'
            };

            this.articleService.updateArticle(this.articleId, request).subscribe({
                next: article => {
                    this.isSaving = false;
                    this.saved.emit(article);
                },
                error: err => {
                    console.error('Failed to update article', err);
                    this.isSaving = false;
                }
            });
        } else {
            const request: HelpArticleCreateRequestDto = {
                title: this.title,
                content: this.content,
                summary: this.summary,
                moduleAlias: this.moduleAlias,
                categoryId: this.selectedCategoryId,
                sortOrder: this.sortOrder,
                tagsJson: this.tagsJson,
                changeNote: this.changeNote || 'Artikel erstellt'
            };

            this.articleService.createArticle(request).subscribe({
                next: article => {
                    this.isSaving = false;
                    this.saved.emit(article);
                },
                error: err => {
                    console.error('Failed to create article', err);
                    this.isSaving = false;
                }
            });
        }
    }

    onCancel(): void {
        this.cancelled.emit();
    }

    private flattenCategories(categories: HelpCategoryDto[], depth: number = 0): HelpCategoryDto[] {
        const result: HelpCategoryDto[] = [];
        for (const cat of categories) {
            const flatCat = {...cat, name: '  '.repeat(depth) + cat.name};
            result.push(flatCat);
            if (cat.children && cat.children.length > 0) {
                result.push(...this.flattenCategories(cat.children, depth + 1));
            }
        }
        return result;
    }
}
